/**
 * Passive GPT + Prebid observers → ad funnel events.
 *
 * One `/p` per rendered slot (consumer: covered impression + revenue), sent
 * from GPT `slotRenderEnded` and priced from the slot's `hb_pb` targeting.
 *
 * `bid` is the header-bidding price on an HB fill and the floor the backfill
 * outbid on a backfill fill — the consumer predicts backfill revenue from it.
 */

import { deferKzqAdEvent, type KzqAdEvent } from '../../core/kzq';
import {
  track,
  trackPrediction,
  trackPredictionForValue,
  type EventParams,
} from '../../core/analytics/track';
import { trackBid } from '../../core/analytics/sessionValue';
import { getSdkStateOrNull } from '../../core/state';
import { warn } from '../../util/log';
import { whenGptReady, whenPrebidReady } from './adGlobals';
import { MBP_EVENTS, RevenueGroup } from './events';
import type {
  GptPubAdsService,
  GptSlot,
  GptSlotEvent,
  GptSlotRenderEndedEvent,
  PrebidBidWon,
} from './types';

/** Fallback when a win carries no `ttl` — Prebid's conventional default. */
const DEFAULT_BID_TTL_S = 300;

interface Win {
  cpm: number;
  bidder: string;
  expiresAt: number;
}

interface SlotWinner {
  bidder: string;
  bid: number;
  adomain: string;
}

let wonByAdUnit: Record<string, Win> = {};
let renderedWinnerBySlotId: Record<string, string> = {};
let pubadsRef: GptPubAdsService | null = null;

const slotTargeting = (slot: GptSlot, key: string): string => {
  try {
    return slot.getTargeting(key)?.[0] ?? '';
  } catch {
    return '';
  }
};

/** Winner from GPT slot targeting (hb_bidder / hb_pb / hb_adomain). */
const parseSlotWinner = (slot: GptSlot): SlotWinner => ({
  bidder: slotTargeting(slot, 'hb_bidder'),
  bid: parseFloat(slotTargeting(slot, 'hb_pb')) || 0,
  adomain: slotTargeting(slot, 'hb_adomain'),
});

/** `mod=` on `/p`; unset means the consumer applies 1.0. */
const predictionModifier = (): number | undefined => {
  try {
    return getSdkStateOrNull()?.config.analytics.predictionModifier;
  } catch {
    return undefined;
  }
};

/**
 * Every backfill fill is ADX. Nothing on the event separates the backfill
 * advertisers without the campaign map: GPT sets `yieldGroupIds` AND `companyIds`
 * on ordinary AdX backfill, so reading either as an Exchange Bidding marker sends
 * all of AdX to EBDA and leaves `adx_impressions` / `adx_revenue` at zero
 * (RND-11805). yoto-web reports the same fills as ADX because its
 * `dfpCampaignAdvertisers` branch returns before it ever reads the markers.
 *
 * Cost: a real EBDA or ADSENSE fill is reported as ADX. That matches yoto-web's
 * numbers on this inventory, where EBDA is ~0.
 *
 * Re-enable the branches below — with `campaignAdvertiser` — when the map carries
 * live campaigns for the network being observed. The markers are only meaningful
 * after the map has already named the campaign.
 */
const backfillGroup = (_event: GptSlotRenderEndedEvent): RevenueGroup => {
  // const advertiser = campaignAdvertiser(_event.campaignId);
  // if (advertiser === 'adx') {
  //   return _event.companyIds?.length ? RevenueGroup.EBDA : RevenueGroup.ADX;
  // }
  // if (_event.yieldGroupIds?.length) return RevenueGroup.EBDA;
  // if (advertiser === 'adsense') return RevenueGroup.ADSENSE;
  // if (advertiser === 'ebda') return RevenueGroup.EBDA;
  return RevenueGroup.ADX;
};

// Paired with the disabled branches in `backfillGroup`.
// const campaignAdvertiser = (campaignId: number | undefined): string | undefined => {
//   if (campaignId === undefined) return undefined;
//   try {
//     return getSdkStateOrNull()?.config.analytics.dfpCampaignAdvertisers?.[String(campaignId)];
//   } catch {
//     return undefined;
//   }
// };

/** TAM codes that carry no rate — the same values the prediction aggregation excludes. */
const UNPRICED_A9_CODES = ['', '0', '1', '2'];

/**
 * A9's `amznbid` is a code, not a price. apstag normally writes it per slot, but
 * some wrappers set it page-level, so both are read. Returns the code and where
 * it came from; empty code means A9 exposed nothing by render time.
 */
const readA9Code = (slot: GptSlot): { code: string; priced: boolean } => {
  let code = '';
  for (const key of ['amznbid', 'amznbid_sp']) {
    code = slotTargeting(slot, key);
    if (code) break;
  }
  if (!code) {
    try {
      code = pubadsRef?.getTargeting?.('amznbid')?.[0] ?? '';
    } catch {
      code = '';
    }
  }
  return { code, priced: !!code && UNPRICED_A9_CODES.indexOf(code) === -1 };
};

const ADVERTISER_BY_GROUP: Partial<Record<RevenueGroup, string>> = {
  [RevenueGroup.ADX]: 'adx',
  [RevenueGroup.ADSENSE]: 'adsense',
  [RevenueGroup.EBDA]: 'ebda',
};

/** Prebid `adUnitCode` maps to either the GPT ad-unit-path or the slot element id. */
const lookupWin = (slot: GptSlot): Win | null => {
  try {
    const won = wonByAdUnit[slot.getAdUnitPath()] ?? wonByAdUnit[slot.getSlotElementId()];
    return won && Date.now() < won.expiresAt ? won : null;
  } catch {
    return null;
  }
};

const reportRender = (
  event: GptSlotRenderEndedEvent,
  isBackfill: boolean,
  winner: SlotWinner,
): void => {
  const hbBid = winner.bid;
  const group = isBackfill ? backfillGroup(event) : RevenueGroup.HEADER_BIDDING;
  const bidder = winner.bidder;

  // On `/p` the consumer substitutes the advertiser itself, but on `/dye` it
  // records whatever the client sent — so `adview` has to carry the advertiser
  // for a backfill fill, or the impression counts against the outbid bidder.
  try {
    renderedWinnerBySlotId[event.slot.getSlotElementId()] =
      (isBackfill ? ADVERTISER_BY_GROUP[group] : bidder) ?? bidder;
  } catch {
    /* ignore */
  }

  if (isBackfill) {
    let adUnitPath = '';
    try {
      adUnitPath = event.slot.getAdUnitPath() ?? '';
    } catch {
      /* ignore */
    }
    // hb_pb on the slot means Prebid ran and lost to the backfill; no hb_pb
    // means the backfill filled with no bid to beat. One event per backfill
    // render — the beat_hb/no_hb ratio is derived downstream.
    const hasHbBid = hbBid > 0;

    track(hasHbBid ? MBP_EVENTS.backfillBeatHb : MBP_EVENTS.backfillNoHb, {
      info: adUnitPath,
      bidder: ADVERTISER_BY_GROUP[group] ?? '',
      pba: hasHbBid ? bidder || undefined : undefined,
      pbb: hasHbBid ? hbBid : undefined,
    });

    // Exactly one of the three, so the three sum to the backfill fills this
    // module saw. Counting the absent case keeps "did A9 bid at all" a measured
    // number rather than a subtraction that quietly absorbs any dropped event.
    const a9 = readA9Code(event.slot);
    const a9Type = !a9.code
      ? MBP_EVENTS.backfillA9None
      : a9.priced
        ? MBP_EVENTS.backfillA9Priced
        : MBP_EVENTS.backfillA9Unpriced;

    track(a9Type, {
      info: adUnitPath,
      bidder: ADVERTISER_BY_GROUP[group] ?? '',
      azb: a9.code || undefined,
    });
  }

  // `rv` asks the consumer to answer with the price it recorded — for backfill
  // that is the prediction, which is the only place the real value appears.
  trackPredictionForValue({
    bidder,
    bid: hbBid,
    group,
    rv: 1,
    at: 'display',
    ns: '0',
    mod: predictionModifier(),
    ba: bidder && !isBackfill ? bidder : undefined,
    adm: winner.adomain || undefined,
    pba: isBackfill ? bidder || undefined : undefined,
    pbb: isBackfill ? hbBid : undefined,
  }).then((responseText) => {
    const returned = responseText === null ? NaN : parseFloat(responseText);
    trackBid(Number.isFinite(returned) ? returned : hbBid);
  });
};

/**
 * `/p` params for a render reported without state (dying-page emergency flush).
 * Pricing comes from the slot's own targeting (`hb_pb`) and `backfillGroup` reads
 * only the fill itself, so this path needs no config. `rv` and `mod` are
 * deliberately absent — the page is unloading, no response can come back, and
 * the consumer applies its own modifier default.
 */
export function emergencyPredictionParams(event: GptSlotRenderEndedEvent): EventParams {
  if (event.isEmpty) {
    return { bidder: '', bid: 0, group: RevenueGroup.NO_FILL, at: 'display', ns: '0' };
  }
  const isBackfill = !!event.isBackfill;
  const winner = parseSlotWinner(event.slot);
  return {
    bidder: winner.bidder,
    bid: winner.bid,
    group: isBackfill ? backfillGroup(event) : RevenueGroup.HEADER_BIDDING,
    at: 'display',
    ns: '0',
    ba: winner.bidder && !isBackfill ? winner.bidder : undefined,
    adm: winner.adomain || undefined,
    pba: isBackfill ? winner.bidder || undefined : undefined,
    pbb: isBackfill ? winner.bid : undefined,
  };
}

/** GPT `slotRequested` — the consumer's AD_IMPRESSIONS. */
export function handleGptSlotRequested(event: GptSlotEvent): void {
  track(MBP_EVENTS.adRequest, { info: event.slot.getAdUnitPath() ?? '' });
}

/** GPT `slotRenderEnded`. */
export function handleGptSlotRenderEnded(event: GptSlotRenderEndedEvent): void {
  if (event.isEmpty) {
    trackPrediction({ bidder: '', bid: 0, group: RevenueGroup.NO_FILL });
    return;
  }
  reportRender(event, !!event.isBackfill, parseSlotWinner(event.slot));
}

/** Prebid `bidWon`. */
export function handlePrebidBidWon(bid: PrebidBidWon): void {
  let cpm = Number(bid.cpm) || 0;
  try {
    cpm = Number(bid.getCpmInNewCurrency?.('USD')) || cpm;
  } catch {
    /* keep raw cpm */
  }
  if (!bid.adUnitCode) return;
  // expiry follows the bid's own ttl — same rule as pbjs `isBidNotExpired` and `latest`
  const ttlMs = (Number(bid.ttl) || DEFAULT_BID_TTL_S) * 1000;
  const respondedAt =
    Number(bid.responseTimestamp) - (Number(bid.timeToRespond) || 0) || Date.now();
  wonByAdUnit[bid.adUnitCode] = {
    cpm,
    bidder: bid.bidderCode ?? '',
    expiresAt: respondedAt + ttlMs,
  };
}

/** Dispatch one buffered `kzq` entry to its handler. */
export function replayKzqAdEvent(entry: KzqAdEvent): void {
  if (entry.source === 'gpt' && entry.name === 'slotRequested') {
    handleGptSlotRequested(entry.event as GptSlotEvent);
    return;
  }
  if (entry.source === 'gpt' && entry.name === 'slotRenderEnded') {
    handleGptSlotRenderEnded(entry.event as GptSlotRenderEndedEvent);
    return;
  }
  if (entry.source === 'prebid' && entry.name === 'bidWon') {
    handlePrebidBidWon(entry.event as PrebidBidWon);
  }
}

/** Who filled the slot: the advertiser on backfill, else the bidder. */
const winnerBidderFor = (slot: GptSlot): string => {
  try {
    const resolved = renderedWinnerBySlotId[slot.getSlotElementId()];
    if (resolved) return resolved;
  } catch {
    /* ignore */
  }
  const won = lookupWin(slot);
  return (won && won.bidder) || parseSlotWinner(slot).bidder;
};

export function observeGpt(): void {
  whenGptReady((googletag) => {
    try {
      const pubads = googletag.pubads();
      pubadsRef = pubads;

      pubads.addEventListener('slotRequested', (event) => {
        try {
          if (deferKzqAdEvent({ source: 'gpt', name: 'slotRequested', event })) return;
          handleGptSlotRequested(event);
        } catch {
          /* ignore */
        }
      });

      pubads.addEventListener('slotRenderEnded', (event) => {
        try {
          if (deferKzqAdEvent({ source: 'gpt', name: 'slotRenderEnded', event })) return;
          handleGptSlotRenderEnded(event);
        } catch {
          /* ignore */
        }
      });

      pubads.addEventListener('impressionViewable', (event) => {
        try {
          track(MBP_EVENTS.adView, {
            info: event.slot.getAdUnitPath() ?? '',
            bidder: winnerBidderFor(event.slot),
          });
        } catch {
          /* ignore */
        }
      });
    } catch (err) {
      warn('[mbp] gpt observer failed', err);
    }
  });
}

export function observePrebid(): void {
  whenPrebidReady((pbjs) => {
    try {
      // recorded only to label `adview` when the slot exposes no hb_bidder
      pbjs.onEvent?.('bidWon', (bid) => {
        try {
          if (deferKzqAdEvent({ source: 'prebid', name: 'bidWon', event: bid })) return;
          handlePrebidBidWon(bid);
        } catch {
          /* ignore */
        }
      });
    } catch (err) {
      warn('[mbp] prebid observer failed', err);
    }
  });
}

/** Test-only */
export function _resetMbpObserversForTests(): void {
  wonByAdUnit = {};
  renderedWinnerBySlotId = {};
  pubadsRef = null;
}
